import React, { useEffect, useState } from 'react'
import { supabase } from '../../../supabaseClient'

// Matches the real statuses used in OrderManagement.jsx
const STATUS_CONFIG = {
    pending: { label: 'Pending', bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-400' },
    preparing: { label: 'Preparing', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-400' },
    ready: { label: 'Ready', bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-400' },
    completed: { label: 'Completed', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-400' },
    cancelled: { label: 'Cancelled', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-400' },
}

export default function OrdersSummary({ branchId, onViewFull }) {
    const [stats, setStats] = useState(null)
    const [recent, setRecent] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const fetchStats = async () => {
            try {
                // Counts reflect the branch's CURRENT queue (matches the Order
                // Management board), not just orders created today — an order
                // placed yesterday that's still "pending" must still count.
                let query = supabase
                    .from('orders')
                    .select('order_id, order_status, final_amount, created_at, reference_number')
                    .order('created_at', { ascending: false })

                if (branchId) query = query.eq('branch_id', branchId)

                const { data: orders } = await query

                const counts = { pending: 0, preparing: 0, ready: 0, completed: 0, cancelled: 0 }
                    ; (orders || []).forEach(o => {
                        const s = o.order_status?.toLowerCase()
                        if (counts[s] !== undefined) counts[s]++
                    })

                setStats(counts)
                setRecent((orders || []).slice(0, 5))
            } catch (err) {
                console.error('OrdersSummary fetch error:', err)
            } finally {
                setLoading(false)
            }
        }
        fetchStats()

        if (!branchId) return

        // Keep in sync with live order changes the same way OrderManagement does
        const channel = supabase
            .channel(`orders-summary-${branchId}`)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'orders', filter: `branch_id=eq.${branchId}` },
                () => fetchStats()
            )
            .subscribe()

        return () => supabase.removeChannel(channel)
    }, [branchId])

    const gridStatuses = ['pending', 'preparing', 'ready', 'completed']

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 48 48" className="text-blue-600">
                            <g fill="none" stroke="currentColor" strokeWidth="4">
                                <path strokeLinejoin="round" d="M6 15h36l-2 27H8z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16 19V6h16v13" />
                                <path strokeLinecap="round" d="M16 34h16" />
                            </g>
                        </svg>
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">Order Management</h3>
                </div>
                <button
                    onClick={onViewFull}
                    className="text-xs text-[#A0826D] hover:text-[#8B5A3C] font-medium flex items-center gap-1 transition-colors">
                    View Full
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                </button>
            </div>

            <div className="p-5 space-y-4">
                {loading ? (
                    <div className="space-y-3">
                        <div className="flex gap-2">
                            {[1, 2, 3, 4].map(i => <div key={i} className="flex-1 h-14 bg-gray-100 rounded-lg animate-pulse" />)}
                        </div>
                        <div className="h-20 bg-gray-100 rounded-lg animate-pulse" />
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {gridStatuses.map(key => {
                                const cfg = STATUS_CONFIG[key]
                                return (
                                    <div key={key} className={`${cfg.bg} rounded-lg p-3 text-center`}>
                                        <p className={`text-lg font-bold ${cfg.text}`}>{stats?.[key] ?? 0}</p>
                                        <p className={`text-xs font-medium ${cfg.text} mt-0.5`}>{cfg.label}</p>
                                    </div>
                                )
                            })}
                        </div>

                        {recent.length > 0 && (
                            <div>
                                <p className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">Recent Orders</p>
                                <div className="space-y-2">
                                    {recent.map(order => {
                                        const cfg = STATUS_CONFIG[order.order_status?.toLowerCase()] || STATUS_CONFIG.pending
                                        return (
                                            <div key={order.order_id} className="flex items-center justify-between flex-wrap gap-x-2 gap-y-1 py-1.5 px-3 bg-gray-50 rounded-lg">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                                                    <span className="text-xs text-gray-600 font-medium truncate">
                                                        #{order.reference_number || order.order_id?.slice(0, 8)}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3 flex-shrink-0">
                                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text} whitespace-nowrap`}>
                                                        {cfg.label}
                                                    </span>
                                                    <span className="text-xs font-medium text-gray-700 whitespace-nowrap">
                                                        ₱{parseFloat(order.final_amount || 0).toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}